import { createContext, useCallback, useContext, useMemo } from 'react';

import { formatCentsToDollarString } from '@fhs/utils';
import { useToast } from '@fhs-legacy/frontend/src/hooks/use-toast';

import { useUserAccountsQuery } from '../queries/use-user-accounts';

export type GiftCardContext = {
  formattedBalance: string;
  handleBalanceUpdate: () => void;
  handleError: () => void;
  currentBalance: number | undefined;
  isBalanceLoading: boolean;
  lastDigits: string;
  hasGiftCard: boolean;
};

const giftCardContext = createContext<null | GiftCardContext>(null);

export type GiftCardProviderProps = {
  children?: React.ReactNode;
};

export const GiftCardProvider = (props: GiftCardProviderProps) => {
  const { data: userAccountsData, isFetching, refetch } = useUserAccountsQuery();
  const giftCard = userAccountsData?.accounts?.find(account => account.giftCard)?.giftCard;

  const currentBalance = giftCard?.currentBalance;

  const formattedBalance = useMemo(
    () => (currentBalance !== undefined ? `$${formatCentsToDollarString(currentBalance)}` : ''),
    [currentBalance]
  );

  const { show } = useToast();

  const handleBalanceUpdate = useCallback(() => {
    refetch();
    show({
      variant: 'positive',
      text: 'Digital gift card funds updated!',
    });
  }, [refetch, show]);

  const handleError = useCallback(() => {
    show({
      variant: 'negative',
      text: 'Error updating digital gift card funds',
    });
  }, [show]);

  return (
    <giftCardContext.Provider
      value={{
        formattedBalance,
        handleBalanceUpdate,
        handleError,
        currentBalance,
        isBalanceLoading: isFetching,
        lastDigits: giftCard?.last4 ?? '',
        hasGiftCard: Boolean(giftCard),
      }}
    >
      {props.children}
    </giftCardContext.Provider>
  );
};

export function useGiftCardContext() {
  const context = useContext(giftCardContext);

  if (!context) {
    throw new Error('Must consume `useGiftCardContext` with a GiftCardProvider');
  }

  return context;
}
